import React, { ChangeEvent, FC, SyntheticEvent, useContext, useState } from 'react';
import { IGMBReview } from '../../../shared/interfaces/IGMBReview';
import styled from 'styled-components';
import { Box, Button, Checkbox, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import StarRating from '../../StarRating/StarRating';
import Helper from '../../../shared/helpers/Helper';
import { InfoOutlined, MarkChatRead, MoreVert, RateReview } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TabPanel from '../../TabPanel/TabPanel';
import GMBReviewReply from '../ReviewReply/GMBReviewReply';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import Note from '../../Notes/Note';
import DeleteReview from '../../Review/DeleteReview';
import { AuthContext } from '../../../providers/AuthProvider';
import { customColors } from '../../../constants/theme';

const ReviewContainer = styled.div<{ $checked: boolean }>`
  padding: ${(props) => props.theme.spacing(1)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
  border: 1px solid
    ${(props) => (props.$checked ? props.theme.palette.primary.main : props.theme.palette.divider)};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
  background-color: ${(props) =>
    props.$checked
      ? `rgba(${customColors.primary.main}, 0.1)`
      : props.theme.palette.background.paper};
`;

const ReviewContent = styled.div`
  padding-top: ${(props) => props.theme.spacing(1)};
  padding-bottom: ${(props) => props.theme.spacing(1)};
  padding-left: ${(props) => props.theme.spacing(7)};
  padding-right: ${(props) => props.theme.spacing(7)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  padding-right: ${(props) => props.theme.spacing(7)};
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const ReviewFooter = styled.div`
  display: flex;
  flex-direction: column;
`;

interface Props extends IGMBReview {
  rating: number;
  postgresLocationId: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  communityManagementSettings: CommunityManagement;
  note: string;
  deletion: boolean;
  id: string;
  isUpdated: boolean;
  currentUpdatedStatus: boolean;
}

const GMBReview: FC<Props> = ({
  rating,
  id,
  comment,
  createTime,
  reviewId,
  reviewReply,
  reviewer,
  updateTime,
  checked,
  postgresLocationId,
  onChange,
  communityManagementSettings,
  note,
  deletion,
  currentUpdatedStatus,
  deleted,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  const [showFullText, setShowFullText] = useState(false);
  const [currentTab, setCurrentTab] = useState<number | boolean>(false);

  const handleTabChange = (event: SyntheticEvent, newValue: number | boolean) => {
    setCurrentTab(newValue);
  };

  const handleTabPanelClose = () => {
    setCurrentTab(false);
  };

  return (
    <ReviewContainer $checked={checked}>
      <ReviewHeader>
        <Checkbox
          disabled
          id={reviewId}
          checked={checked}
          onChange={onChange}
          sx={{ mr: theme.spacing(1), display: 'none' }}
        />
        <StarRating
          value={rating}
          sx={{ ml: theme.spacing(7) }}
        />
        <Box sx={{ display: 'flex', ml: theme.spacing(1), cursor: 'pointer' }}>
          <Tooltip
            title={`${t('info_rating')}: ${rating}`}
            placement="top"
            arrow
          >
            <InfoOutlined
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </Tooltip>
        </Box>
        <Tooltip
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2">
                {t('written')} {Helper.getFormattedDate(createTime, false)}
              </Typography>
              {currentUpdatedStatus && (
                <Typography variant="body2">
                  {t('refreshed')} {Helper.getFormattedDate(updateTime, false)}
                </Typography>
              )}
            </Box>
          }
          arrow
        >
          <Typography
            variant="body2"
            sx={{ ml: 'auto', cursor: 'pointer' }}
          >
            {Helper.getFormattedDate(createTime, true)}
          </Typography>
        </Tooltip>
        {hasRole('can-use-note') ? (
          <>
            {(note || id) && (
              <Note
                note={note}
                id={id}
                type="gmbreview"
              />
            )}
          </>
        ) : null}
        {hasRole('can-use-deletion') ? (
          <>
            {(deletion || id) && (
              <DeleteReview
                id={id}
                deletion={deletion}
              />
            )}
          </>
        ) : null}
      </ReviewHeader>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: theme.spacing(2),
          pl: theme.spacing(7),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mr: theme.spacing(1),
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={reviewer.profilePhotoUrl}
            width="42"
            referrerPolicy="no-referrer"
            alt="Google avatar"
          />
        </Box>
        <Box>
          <Typography variant="body1">{reviewer.displayName}</Typography>
        </Box>
      </Box>
      {comment && (
        <ReviewContent>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1">
              {showFullText ? comment : Helper.shortText(comment, 200)}
            </Typography>
            {comment.length > 200 && (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MoreVert />}
                  onClick={() => setShowFullText((prevState) => !prevState)}
                  sx={{
                    my: theme.spacing(2),
                  }}
                >
                  {showFullText ? `${t('show_less')}` : `${t('show_more')}`}
                </Button>
              </Box>
            )}
          </Box>
        </ReviewContent>
      )}
      <ReviewFooter>
        <Box sx={{ pl: theme.spacing(7), width: '100%' }}>
          <Box sx={{ borderBottom: currentTab === false ? 0 : 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label={t('comment_actions')}
            >
              <Tab
                icon={reviewReply === null ? <RateReview /> : <MarkChatRead />}
                aria-label={t('comment')}
              />
            </Tabs>
          </Box>
          <TabPanel
            heading={`${t('comment')}`}
            value={currentTab}
            index={0}
            onCloseClick={handleTabPanelClose}
          >
            <GMBReviewReply
              reviewerName={reviewer.displayName}
              comment={comment}
              locationId={postgresLocationId}
              reviewId={reviewId}
              reviewReply={reviewReply}
              communityManagementSettings={communityManagementSettings}
              input={comment}
            />
          </TabPanel>
        </Box>
      </ReviewFooter>
    </ReviewContainer>
  );
};

export default GMBReview;
